/**
 * Stripe's payment page inside the game (ADR 0031). The card form must come
 * from Stripe's own iframe, served by js.stripe.com: Stripe forbids bundling
 * Stripe.js, and that iframe is what keeps card numbers out of our pages. So
 * the script is loaded on demand, the first time someone pays, and nothing
 * here touches a card: it only mounts what Stripe draws.
 */

/** Stripe.js built for the account's API version (`embedded_page` sessions). */
const STRIPE_JS_URL = 'https://js.stripe.com/dahlia/stripe.js';

/** The embedded page, once created: mounted into an element, destroyed when done. */
export interface EmbeddedCheckout {
  mount: (element: HTMLElement) => void;
  destroy: () => void;
}

interface EmbeddedCheckoutOptions {
  clientSecret: string;
  onComplete: () => void;
}

interface StripeInstance {
  createEmbeddedCheckoutPage?: (options: EmbeddedCheckoutOptions) => Promise<EmbeddedCheckout>;
  initEmbeddedCheckout?: (options: EmbeddedCheckoutOptions) => Promise<EmbeddedCheckout>;
}

type StripeFactory = (publishableKey: string) => StripeInstance;

let loading: Promise<StripeFactory> | undefined;

function factoryOnWindow(): StripeFactory | undefined {
  const candidate: unknown = (window as unknown as { Stripe?: unknown }).Stripe;
  return typeof candidate === 'function' ? (candidate as StripeFactory) : undefined;
}

function loadStripe(): Promise<StripeFactory> {
  const ready = factoryOnWindow();
  if (ready !== undefined) return Promise.resolve(ready);
  loading ??= new Promise<StripeFactory>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = STRIPE_JS_URL;
    script.async = true;
    script.addEventListener('load', () => {
      const factory = factoryOnWindow();
      if (factory === undefined) reject(new Error('Stripe.js loaded without window.Stripe'));
      else resolve(factory);
    });
    script.addEventListener('error', () => {
      // A blocked script (ad blocker, offline) may be retried by the next purchase.
      loading = undefined;
      reject(new Error('Stripe.js could not be loaded'));
    });
    document.head.append(script);
  });
  return loading;
}

/**
 * Create the embedded payment page for a Checkout Session. `onComplete` is
 * Stripe telling us the payment went through in the page; the items still
 * arrive by the webhook, which the caller then waits for.
 */
export async function createEmbeddedCheckout(
  publishableKey: string,
  clientSecret: string,
  onComplete: () => void,
): Promise<EmbeddedCheckout> {
  const stripe = (await loadStripe())(publishableKey);
  // Newer builds renamed the method; older ones only have the first name.
  const create = stripe.createEmbeddedCheckoutPage ?? stripe.initEmbeddedCheckout;
  if (create === undefined) throw new Error('This Stripe.js has no embedded checkout');
  return create.call(stripe, { clientSecret, onComplete });
}
